// ABOUTME: SPEC-52 — the waitlist form's four inputs plus the variant chip row, split out of
// ABOUTME: WaitlistModal.js so the shell stays chrome-only (D-13) and this stays data-driven.

import React from 'react';

// One row per field, in display order. `labelKey`/`errorKey` are the backend's own snake_case
// contract (`Versai-ophion/config/waitlist.py:91-104`'s `modal` mapping) — never re-cased here,
// so a mismatch between this file and the server payload is impossible by construction rather
// than by two keyed lists staying in sync (review finding BLOCKER 1, 2026-09-10). `error_name`
// is frontend-only (the backend never validates a name), so it is not in that mapping.
// `placeholder` is presentation-only hint text (never a value, never sent) — not part of the
// backend's `modal` mapping, so it stays a frontend literal rather than a tenant-config key.
const FIELD_ROWS = [
  { key: 'name', type: 'text', labelKey: 'name_label', errorKey: 'error_name', placeholder: null },
  { key: 'email', type: 'email', labelKey: 'email_label', errorKey: 'error_email', placeholder: null },
  { key: 'phone', type: 'tel', labelKey: 'phone_label', errorKey: null, placeholder: null },
  {
    key: 'message', type: 'textarea', labelKey: 'message_label', errorKey: null,
    placeholder: "Any particular variant or timing you'd like the house to know about?",
  },
];

function VariantChips({ variants, selected, onToggle, geometry, unit, font, disabled }) {
  if (!variants.length) return null;
  return (
    <div className="flex flex-wrap" style={{ gap: unit(geometry.gap), marginBottom: unit(geometry.gap * 2) }}>
      {variants.map((v) => {
        const active = selected.includes(v.id);
        // `relative` (prod 2026-10-14): `.reel-touch-target::after` is absolutely positioned, so an
        // unpositioned chip let its 44px hit box size itself against the PANEL and swallow every tap
        // on the fields below — no keyboard on a phone whenever a chip was shown.
        return (
          <button
            key={v.id}
            type="button"
            disabled={disabled}
            aria-pressed={active}
            onClick={() => onToggle(v.id)}
            className={`relative reel-touch-target uppercase font-reel-body transition-opacity ${
              active ? 'bg-reel-cta-primaryBg text-reel-chrome-textInk' : 'bg-white/10 text-reel-chrome-textPrimary'
            }`}
            style={{
              height: unit(geometry.h), borderRadius: unit(geometry.radius),
              paddingLeft: unit(geometry.padX), paddingRight: unit(geometry.padX),
              fontSize: font,
            }}
          >
            {v.title}
          </button>
        );
      })}
    </div>
  );
}

function InputRow({ row, value, error, onChange, geometry, unit, type, copy, disabled }) {
  const label = copy[row.labelKey] || row.key;
  const isTextarea = row.type === 'textarea';
  const Tag = isTextarea ? 'textarea' : 'input';
  return (
    <label className="flex flex-col" style={{ marginBottom: unit(geometry.gap) }}>
      <span
        className="font-reel-body uppercase tracking-widest text-reel-chrome-textDim"
        style={{ fontSize: type(geometry.label), marginBottom: unit(8) }}
      >
        {label}
      </span>
      <Tag
        type={isTextarea ? undefined : row.type}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(row.key, e.target.value)}
        placeholder={row.placeholder || undefined}
        rows={isTextarea ? 3 : undefined}
        aria-invalid={!!error}
        className="bg-white/5 border border-white/10 text-reel-chrome-textPrimary font-reel-body focus:outline-none focus:border-white/30"
        style={{
          height: isTextarea ? unit(geometry.h * 2) : unit(geometry.h),
          borderRadius: unit(geometry.radius),
          paddingLeft: unit(geometry.padX), paddingRight: unit(geometry.padX),
          fontSize: type(geometry.input),
          resize: 'none',
        }}
      />
      {error && row.errorKey && (
        <span
          className="font-reel-body text-red-300"
          style={{ fontSize: type(geometry.error), marginTop: unit(8) }}
        >
          {copy[row.errorKey] || 'Required'}
        </span>
      )}
    </label>
  );
}

/**
 * @param {{
 *   fields: object, errors: object, onFieldChange: (key: string, value: string) => void,
 *   variants: {id:string,title:string}[], selected: string[], onToggleVariant: (id:string) => void,
 *   copy: object, geometry: object, chipGeometry: object, unit: (n:number) => string,
 *   type: (n:number) => string, disabled?: boolean,
 * }} props
 */
export default function WaitlistFields({
  fields, errors, onFieldChange, variants, selected, onToggleVariant,
  copy, geometry, chipGeometry, unit, type, disabled = false,
}) {
  return (
    <div>
      <VariantChips
        variants={variants} selected={selected} onToggle={onToggleVariant}
        geometry={chipGeometry} unit={unit} font={type(chipGeometry.font)} disabled={disabled}
      />
      {FIELD_ROWS.map((row) => (
        <InputRow
          key={row.key} row={row} value={fields[row.key] || ''} error={errors[row.key]}
          onChange={onFieldChange} geometry={geometry} unit={unit} type={type} copy={copy} disabled={disabled}
        />
      ))}
    </div>
  );
}
